import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { resolveDefaultProjectId } from '../services/projectService';

/**
 * Where /workspace goes: straight into the user's default project.
 *
 * This route exists so that login, the root path and the sidebar all resolve
 * the default project the same way, and so a project id never has to be
 * resolved before a navigation can be issued.
 *
 * Every hop replaces its entry — Back should return to wherever the user came
 * from, not bounce back through the resolver and forward again.
 */
const DefaultProjectRedirect: React.FC = () => {
    // undefined while resolving; null once resolved to "nowhere in particular".
    const [projectId, setProjectId] = useState<string | null | undefined>(undefined);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        let active = true;
        resolveDefaultProjectId()
            .then((id) => { if (active) setProjectId(id); })
            .catch((error) => {
                console.error('Failed to resolve default project:', error);
                if (active) setFailed(true);
            });
        return () => { active = false; };
    }, []);

    // No org means no session to resolve against.
    if (!localStorage.getItem('org_id')) return <Navigate to="/login" replace />;

    // Nothing to open, or we could not find out: the hub is where projects are
    // listed and created, so it is the honest landing place for both.
    if (failed || projectId === null) return <Navigate to="/intelligence-hub" replace />;

    if (projectId) return <Navigate to={`/analytics/projects/${projectId}`} replace />;

    return (
        <div className="flex h-screen items-center justify-center bg-page">
            <span className="inline-block w-8 h-8 border-2 border-line border-t-accent-fg rounded-full animate-spin" />
        </div>
    );
};

export default DefaultProjectRedirect;
